import React, { useState, useEffect } from 'react';
import styles from './StudyInformations.module.sass';
import { EditorState, ContentState } from 'draft-js';
import cn from 'classnames';
import Card from '../../../../components/Card';
import TextInput from '../../../../components/TextInput';
import Dropdown from '../../../../components/Dropdown';
import Switch from '../../../../components/Switch';
import Editor from '../../../../components/Editor';



import { getFormations } from '../../../../services/formation.service';
import { getCenters } from '../../../../services/center.service';


const SOURCE_OPTIONS = ['Indeed', 'LinkedIn', 'Site Web', 'Parrainage', 'Salon', 'Autre'];

const SITUATION_OPTIONS = [
    { value: '1', title: '1 - Scolaire' },
    { value: '2', title: '2 - Prépa apprentissage' },
    { value: '3', title: '3 - Etudiant' },
    { value: '4', title: '4 - Contrat d\'apprentissage' },
    { value: '5', title: '5 - Contrat de professionnalisation' },
    { value: '6', title: '6 - Contrat aidé' },
    { value: '7', title: '7 - En formation au CFA avant signature d\'un contrat d\'apprentissage (L6222-12-1 du code du travail)' },
    { value: '8', title: '8 - En formation, au CFA, sans contrat, suite à rupture (5° de L6231-2 du code du travail)' },
    { value: '9', title: '9 - Stagiaire de la formation professionnelle' },
    { value: '10', title: '10 - Salarié' },
    { value: '11', title: '11 - Personne à la recherche d\'un emploi (inscrite ou non au Pôle Emploi)' },
    { value: '12', title: '12 - Inactif' }
];

const HIGHEST_DIPLOMA_OPTIONS = [
    "13 - Aucun diplôme ni titre professionnel",
    "25 - Diplôme national du brevet (DNB)",
    "26 - Certificat de formation générale",
    "38 - Autre diplôme ou titre de niveau CAP/BEP",
    "35 - Mention complémentaire",
    "34 - BEP",
    "33 - CAP",
    "49 - Autre diplôme ou titre de niveau bac",
    "43 - Baccalauréat Technologique",
    "42 - Baccalauréat Général",
    "41 - Baccalauréat Professionnel",
    "58 - Autre diplôme ou titre de niveau bac+2",
    "55 - Diplôme Universitaire de technologie",
    "54 - Brevet de Techniciens supérieurs",
    "69 - Autre diplôme ou titre de niveau bac +3 ou 4",
    "64 - Bachelor Universitaire  ed technologie BUT",
    "63 - Licence générale",
    "62 - Licence professionnelle",
    "61 - 1ère année de Master",
    "79 - Autre diplôme ou titre de niveau bac +5 ou plus",
    "74 - Diplôme d'ingénieur, diplôme de commerce",
    "73 - Master indifférencié",
    "72 - Master recherche/DEA",
    "71 - Master professionnel/DESS",
    "80 - Doctorat"
];

const StudyInformations = ({ prospect, onProspectChange, onDropdownChange, onSwitchChange, loading }) => {
    const [formations, setFormations] = useState([]);
    const [loadingFormations, setLoadingFormations] = useState(false);
    const [centers, setCenters] = useState([]);
    const [loadingCenters, setLoadingCenters] = useState(false);
    // Utiliser useRef pour stocker l'EditorState et éviter les re-rendus inutiles
    const editorStateRef = React.useRef(null);
    
    // Initialiser l'EditorState une seule fois
    const [content, setContent] = useState(() => {
        const remarks = typeof prospect?.remarks === 'string' ? prospect.remarks : '';
        const editorState = EditorState.createWithContent(ContentState.createFromText(remarks));
        editorStateRef.current = editorState;
        return editorState;
    });

    // Mettre à jour le contenu des remarques quand le prospect change, mais seulement si nécessaire
    // et en préservant la sélection et le focus
    useEffect(() => {
        if (prospect?.remarks && editorStateRef.current) {
            const currentContent = editorStateRef.current.getCurrentContent().getPlainText();
            const newRemarks = typeof prospect?.remarks === 'string' ? prospect.remarks : '';
            
            // Ne mettre à jour que si le contenu a réellement changé et est différent
            // de ce que l'utilisateur est en train d'éditer
            if (newRemarks !== currentContent && !editorStateRef.current.getSelection().hasFocus) {
                const newState = EditorState.createWithContent(ContentState.createFromText(newRemarks));
                editorStateRef.current = newState;
                setContent(newState);
            }
        }
    }, [prospect?.remarks]);

    // Fonction personnalisée pour mettre à jour le contenu de l'éditeur sans mise à jour automatique
    const handleEditorChange = (newEditorState) => {
        // Stocker la référence de l'état de l'éditeur
        editorStateRef.current = newEditorState;
        setContent(newEditorState);
        // Pas de mise à jour automatique des données du prospect
    };
    
    // Fonction pour synchroniser les remarques avec le prospect avant la soumission
    const syncRemarksBeforeSubmit = () => {
        if (editorStateRef.current) {
            const remarks = editorStateRef.current.getCurrentContent().getPlainText();
            if (remarks !== prospect?.remarks) {
                onDropdownChange('remarks', remarks);
            }
        }
    };
    
    // Exposer la fonction de synchronisation au composant parent
    React.useEffect(() => {
        // Ajouter un écouteur d'événement sur le formulaire pour capturer la soumission
        const form = document.querySelector('form');
        if (form) {
            const handleSubmit = (e) => {
                syncRemarksBeforeSubmit();
            };
            form.addEventListener('submit', handleSubmit, true);
            return () => {
                form.removeEventListener('submit', handleSubmit, true);
            };
        }
    }, []);

    useEffect(() => {
        const fetchFormations = async () => {
            setLoadingFormations(true);
            try {
                const data = await getFormations();
                setFormations(data || []);
            } catch (error) {
                console.error("Erreur lors de la récupération des formations", error);
                setFormations([]);
            } finally {
                setLoadingFormations(false);
            }
        };

        const fetchCenters = async () => {
            setLoadingCenters(true);
            try {
                const result = await getCenters({ perPage: 100, fullData: false });
                setCenters(result.items || []);
            } catch (error) {
                console.error("Erreur lors de la récupération des centres", error);
                setCenters([]);
            } finally {
                setLoadingCenters(false);
            }
        };

        fetchFormations();
        fetchCenters();
    }, []);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    };



    const formationOptions = formations.map(f => ({ value: f.id, title: f.exact_title_of_qualification }));
    const centerOptions = centers.map(c => ({ value: c.id, title: c.name }));
    return (
        <Card className={styles.card} title="Études & Projet" classTitle="title-green">


            <div className={styles.group}>

                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Source"
                        name="source"
                        required
                        options={SOURCE_OPTIONS}
                        value={SOURCE_OPTIONS.find(opt => opt === prospect?.source) || ''}
                        setValue={(option) => onDropdownChange('source', option)}
                    />
                </div>


                <div className={styles.field}>
                    {loadingFormations ? (
                        <p>Chargement des formations...</p>
                    ) : (
                        <Dropdown
                            className={styles.dropdown}
                            label="Formation visée"
                            name="formation"
                            options={formationOptions}
                            value={formationOptions.find(opt => opt.value === prospect?.desired_formations) || ''}
                            setValue={(option) => onDropdownChange('desired_formations', option.value)}
                            required
                        />
                    )}
                </div>

                <TextInput
                    className={styles.field}
                    label="Date de début souhaitée"
                    name="birth_date"
                    type="date"
                    value={formatDateForInput(prospect?.desired_start_date)}
                    onChange={onProspectChange}
                />

                <div className={styles.field}>
                    {loadingCenters ? (
                        <div>Chargement des centres...</div>
                    ) : (
                        <Dropdown
                            className={styles.dropdown}
                            label="Campus"
                            name="campus"
                            options={centerOptions}
                            value={centerOptions.find(opt => opt.value === prospect?.campus_preference) || ''}
                            setValue={(option) => onDropdownChange('campus_preference', option.value)}
                        />
                    )}
                </div>

                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Situation actuelle"
                        name="situation_before_contract"
                        required
                        options={SITUATION_OPTIONS}
                        value={SITUATION_OPTIONS.find(opt => opt.value === prospect?.current_situation) || ''}
                        setValue={(option) => onDropdownChange('current_situation', option.value)}
                    />
                </div>

                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Niveau de diplôme"
                        name="diploma_level"
                        required
                        options={HIGHEST_DIPLOMA_OPTIONS}
                        value={HIGHEST_DIPLOMA_OPTIONS.find(opt => opt === prospect?.diploma_level) || ''}
                        setValue={(option) => onDropdownChange('diploma_level', option)}
                    />
                </div>

            </div>
            <div className={styles.divider} />

            <div className={styles.group}>
                <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginLeft: '10px' }}>Disponible immédiatement</span>
                    <Switch
                        name="availability"
                        value={prospect?.availability || false}
                        onChange={(value) => onSwitchChange('availability', value)}
                        className={styles.switch}
                    />
                </div>

                <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginLeft: '10px' }}>Intéressé par l'alternance :</span>
                    <Switch
                        name="interested_in_alternance"
                        value={prospect?.interested_in_alternance || false}
                        onChange={(value) => onSwitchChange('interested_in_alternance', value)}
                        className={styles.switch}
                    />
                </div>
            </div>

            {prospect?.interested_in_alternance && (
                <div className={styles.group}>
                    <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginLeft: '10px' }}>A des pistes d'alternance :</span>
                        <Switch
                            name="has_alternance_leads"
                            value={prospect?.has_alternance_leads || false}
                            onChange={(value) => onSwitchChange('has_alternance_leads', value)}
                            className={styles.switch}
                        />
                    </div>
                    {prospect?.has_alternance_leads && (
                        <TextInput
                            className={styles.field}
                            label="Détails des pistes"
                            name="alternance_leads_details"
                            type="text"
                            placeholder="Précisez les pistes d'alternance"
                            value={prospect?.alternance_leads_details || ''}
                            onChange={onProspectChange}
                        />
                    )}
                </div>
            )}



            <div className={styles.divider} />

            <Editor
                state={content}
                onChange={handleEditorChange}
                classEditor={styles.editor}
                label="Remarque(s)"
                tooltip="Informations complémentaires sur le prospect"
                placeholder="Ajoutez vos remarques ici..."
            />


            <div className={styles.divider}></div>

            <div className={styles.actions}>
                <button
                    className={cn("button", styles.submitButton)}
                    type="submit"
                    disabled={loading}
                >
                    {loading ? "Mise à jour en cours..." : "Mettre à jour le prospect"}
                </button>
            </div>
        </Card>
    );
};

export default StudyInformations;